import { createContext, useContext, useReducer, useRef, useCallback, useEffect } from 'react';

const GameContext = createContext(null);

const ROOM_WORKER_URL = import.meta.env.VITE_ROOM_WORKER_URL ?? 'https://saiten-man-room-worker.engineer-445.workers.dev';

const initialState = {
  // 接続・認証
  roomCode: null,
  participantId: null,
  role: null, // 'host' | 'participant'
  token: null,
  wsStatus: 'disconnected', // 'disconnected' | 'connecting' | 'connected'

  // ルーム状態（Worker から room:state で受け取る）
  room: null,

  // ホスト採点用に受け取った回答（answer:reveal で蓄積）
  currentReveal: null, // { participantId, nickname, emoji, answerType, text, dataUrl }
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SESSION':
      return { ...state, ...action.payload };
    case 'SET_WS_STATUS':
      return { ...state, wsStatus: action.status };
    case 'SET_ROOM':
      return { ...state, room: action.room };
    case 'SET_REVEAL':
      return { ...state, currentReveal: action.reveal };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const wsRef = useRef(null);
  const pingRef = useRef(null);

  const connect = useCallback((roomCode, participantId, token) => {
    if (wsRef.current) wsRef.current.close();

    const url = new URL(`${ROOM_WORKER_URL}/api/room/${roomCode}/ws`);
    url.searchParams.set('token', token);

    dispatch({ type: 'SET_SESSION', payload: { roomCode, participantId, token } });
    dispatch({ type: 'SET_WS_STATUS', status: 'connecting' });

    const ws = new WebSocket(url.toString());
    wsRef.current = ws;

    ws.onopen = () => {
      dispatch({ type: 'SET_WS_STATUS', status: 'connected' });
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 20000);
    };

    ws.onmessage = (e) => {
      if (e.data === 'pong') return;
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }

      if (msg.type === 'room:state') {
        dispatch({ type: 'SET_ROOM', room: msg.state });
      } else if (msg.type === 'answer:reveal') {
        dispatch({ type: 'SET_REVEAL', reveal: msg });
      }
      // round:start, answer:received, score:set は room:state に含まれるため個別処理不要
    };

    ws.onclose = () => {
      dispatch({ type: 'SET_WS_STATUS', status: 'disconnected' });
      clearInterval(pingRef.current);
    };

    ws.onerror = () => ws.close();
  }, []);

  const send = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    clearInterval(pingRef.current);
    dispatch({ type: 'RESET' });
  }, []);

  useEffect(() => () => {
    wsRef.current?.close();
    clearInterval(pingRef.current);
  }, []);

  return (
    <GameContext.Provider value={{ state, dispatch, connect, send, disconnect, ROOM_WORKER_URL }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within GameProvider');
  return ctx;
}
